import {
  GenericMutationResult,
  ModelProgramMembershipConnection,
  ProgramMembership,
  ProgramsByUserQuery,
  UpdateProgramMembershipsInput,
  UpdateProgramMembershipsMutation,
  UpdateProgramMembershipsMutationVariables
} from "../../API";
import * as mutations from "../../graphql/mutations";
import * as queries from "../../graphql/queries";
import { genericQuery, listQueryAutoPaginated } from "../api/apiUtils";
import { SimplApiHttpError } from "../../lib/simpl-api-client";
import { API } from "aws-amplify";

export async function fetchMembershipsByUserId(userId: string) {
  const predicate = { programMemberId: userId };
  // Need to call the custom query ListProgramMembershipsByUserId here, or else
  //  the ProgramMembershipDeletion bug reappears (all other Memberships in the
  //  table are deleted upon insert).  Should address the bug in membershipUtils to
  //  avoid this going forward.
  return await listQueryAutoPaginated<
    ProgramsByUserQuery,
    ProgramMembership,
    ModelProgramMembershipConnection
  >(queries.programsByUser, "programsByUser", "items", predicate);
}

/**
 * Carries the HTTP status from a failed membership update so callers can map
 * it to user-facing copy (401/403 vs. generic). Mirrors inviteAPI's typed
 * error pattern.
 */
export class MembershipApiError extends Error {
  readonly status: number;
  // SE-1724: the earliest allowed end date (the involvement floor), present on
  // the 400 the back-date gate returns, so callers can show it to the Sys Admin.
  readonly earliestEndDate?: string;
  /** The refusal's machine-readable code, when the server sent one (SE-3027). */
  readonly code: string | null;
  /** SE-3027: what a confirmed save would remove (code CONFIRM_SCOPED_ACCESS_REMOVAL). */
  readonly removals: ScopedAccessRemoval[];
  constructor(
    message: string,
    status: number,
    extra: {
      earliestEndDate?: string;
      code?: string | null;
      removals?: ScopedAccessRemoval[];
    } = {}
  ) {
    super(message);
    this.name = "MembershipApiError";
    this.status = status;
    this.earliestEndDate = extra.earliestEndDate;
    this.code = extra.code ?? null;
    this.removals = extra.removals ?? [];
    Object.setPrototypeOf(this, MembershipApiError.prototype);
  }
}

/**
 * SE-3027: ending a Rotation Director / Coach membership also removes the
 * person's directorships / coaching assignments in that program. The server
 * refuses the first save with this code and the list below; the admin
 * confirms, and the save is re-sent with `confirmScopedAccessRemoval`.
 */
export const CONFIRM_SCOPED_ACCESS_REMOVAL = "CONFIRM_SCOPED_ACCESS_REMOVAL";

export type ScopedAccessRemoval = {
  programId: string;
  programRole: "RotationDirector" | "Coach";
  /** Rotations (RotationDirector) or mentees (Coach), by id and display name. */
  scopes: { scope_id: string; scope_name: string }[];
};

export async function updateProgramMemberships(
  input: UpdateProgramMembershipsInput,
  options: { confirmScopedAccessRemoval?: boolean } = {}
): Promise<GenericMutationResult | null> {
  const path = `user/${input.userId}/program-memberships`;

  try {
    const response = await API.put("simpl-api", path, {
      body: {
        userId: input.userId,
        programMemberships: input.programMemberships,
        ...(options.confirmScopedAccessRemoval && {
          confirmScopedAccessRemoval: true
        })
      }
    });

    // Response format: { success: true, message } or { success: false, error }
    // 400 here is a placeholder: a `success:false` body only arrives on a 2xx
    // response, which this backend doesn't currently send. Real HTTP statuses
    // (401/403/5xx) arrive via the Amplify rejection handled in catch below.
    if (!response.success) {
      throw new MembershipApiError(
        response.error || "Failed to update program memberships",
        400,
        {
          earliestEndDate:
            typeof response.earliestEndDate === "string"
              ? response.earliestEndDate
              : undefined
        }
      );
    }

    return {
      __typename: "GenericMutationResult",
      success: response.success,
      message: response.message || null,
      error: response.error || null
    };
  } catch (err) {
    if (err instanceof MembershipApiError) throw err;
    // Amplify rejects on non-2xx; pull the status + server message (same shape
    // as inviteAPI's buildInviteApiError) so callers can map 403 to copy.
    const apiError = err as {
      response?: {
        status?: number;
        statusCode?: number;
        data?: {
          error?: string;
          message?: string;
          earliestEndDate?: string;
          code?: string;
          removals?: ScopedAccessRemoval[];
        };
      };
    };
    // `API.put` is patched onto simpl-api-client (lib/api-interceptor.ts),
    // which throws SimplApiHttpError: the status on the error, the parsed
    // body as `.response`. Amplify's own shape (`response.{status,data}`) is
    // kept as the fallback.
    type Payload = NonNullable<NonNullable<typeof apiError.response>["data"]>;
    const simplError = err instanceof SimplApiHttpError ? err : null;
    const status =
      simplError?.status ??
      apiError?.response?.status ??
      apiError?.response?.statusCode ??
      500;
    const payload: Payload | undefined = simplError
      ? typeof simplError.response === "object" && simplError.response !== null
        ? (simplError.response as Payload)
        : undefined
      : apiError?.response?.data;
    const message =
      (typeof payload?.error === "string" && payload.error) ||
      (typeof payload?.message === "string" && payload.message) ||
      (err instanceof Error
        ? err.message
        : "Failed to update program memberships");
    const earliestEndDate =
      typeof payload?.earliestEndDate === "string"
        ? payload.earliestEndDate
        : undefined;
    const code = payload?.code;
    const removals = payload?.removals;
    throw new MembershipApiError(message, status, {
      earliestEndDate,
      code: typeof code === "string" ? code : null,
      removals: Array.isArray(removals) ? removals : []
    });
  }
}
